import { ReactNode, useEffect, useState } from 'react';

import cn from 'classnames';

import { disableScrolling, enableScrolling } from 'components/Header/partials/get_change';
import Render from 'components/render';
import { ALGO_API_PAGE, IS_DEMO } from 'constants/constants';
import routes, { LANDING_PAGE_ROUTES } from 'constants/routes';
import { SIGNAL_TRADING_TUTORIAL_BLOG } from 'constants/urls';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { navigateTo } from 'helpers/url';
import { addBaseURL } from 'helpers/utils';
import i18n from 'i18n/config';
import { getUserId, isCopilotEnabledSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import { AngleRightIcon, ArrowDown, TradeHistoryIcon } from 'styles/assets/icons';
import AlgoApisIcon from 'styles/assets/icons/AlgoApisIcon';
import ApiCopilot from 'styles/assets/icons/ApiCopilot';
import TradingSignalBorderIcon from 'styles/assets/icons/TradingSignalBorderIcon';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';

import { getMixpanelPageName } from '../universalSearch/partials/helpers';
import styles from './algoMenu.module.scss';

type AlgoMenuHead = 'tradingBot' | 'apis' | 'tradeData' | 'apiCopilot';

type AlgoMenuKey = 'label' | 'description';

interface ItemProps {
  id: string;
  label: string;
  href: string;
  description: string;
  contract_type?: string;
  mixpanelLabel: string;
  icon: ReactNode;
  show?: boolean;
  target?: string;
}

const dropdownTranslation = i18n.t('headerFooter:header.algo');

const getLocalizedString = (
  str: AlgoMenuHead,
  key: AlgoMenuKey,
  values?: Record<string, string | number>
): string => {
  return i18n.t(`headerFooter:header.algoMenu.${str}.${key}` as const, { ...values });
};

const AlgoMenuIndia = () => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const userId = useAppSelector(getUserId);
  const isCopilotEnabled = useAppSelector(isCopilotEnabledSelector);

  const algoMenuItems = [
    {
      id: 'apis',
      label: getLocalizedString('apis', 'label'),
      href: ALGO_API_PAGE,
      description: getLocalizedString('apis', 'description'),
      icon: <AlgoApisIcon className={styles.icon} />,
      mixpanelLabel: 'APIs India - Header',
      target: '_self',
    },
    {
      id: 'tradingBot',
      label: getLocalizedString('tradingBot', 'label'),
      href: userId ? `/app${routes.account.signalTrading}` : SIGNAL_TRADING_TUTORIAL_BLOG,
      description: getLocalizedString('tradingBot', 'description'),
      icon: <TradingSignalBorderIcon className={styles.icon} />,
      mixpanelLabel: 'Trading Bot India - Header',
      show: !IS_DEMO,
      target: userId ? '_self' : '_blank',
    },
    {
      id: 'tradeData',
      label: getLocalizedString('tradeData', 'label'),
      href: addBaseURL(routes.tradeHistory),
      description: getLocalizedString('tradeData', 'description'),
      icon: <TradeHistoryIcon className={styles.leaderBoardIcon} />,
      mixpanelLabel: 'Trade Data India - Header',
    },
    {
      id: 'apiCopilot',
      label: getLocalizedString('apiCopilot', 'label'),
      href: LANDING_PAGE_ROUTES.copilot,
      description: getLocalizedString('apiCopilot', 'description'),
      icon: <ApiCopilot width={24} height={24} fill1="var(--main-text-secondary)" />,
      mixpanelLabel: 'API Copilot India - Header',
      show: isCopilotEnabled,
    },
  ];

  useEffect(() => {
    if (isDropdownOpen) {
      disableScrolling();
    } else {
      enableScrolling();
    }
  }, [isDropdownOpen]);

  const onItemClick = (item: ItemProps) => {
    setDropdownOpen(false);
    const { mixpanelLabel, href, target } = item;
    navigateTo(href, target);
    trackMixpanelEvent(mixpanelLabel, {
      page_name: getMixpanelPageName(),
    });
  };

  return (
    <Box
      className={styles.menuContainer}
      onMouseLeave={() => setDropdownOpen(false)}
      onMouseEnter={() => setDropdownOpen(true)}
    >
      <Box
        flex="flex"
        alignItems="center"
        className={cn(styles.menuHeading, {
          [styles.active]: isDropdownOpen,
        })}
      >
        {dropdownTranslation}
        <ArrowDown
          className={cn(styles.arrowDownIcon, {
            [styles.active]: isDropdownOpen,
          })}
        />
      </Box>
      <Render when={isDropdownOpen}>
        <Box
          className={styles.dropdownMenu}
          onMouseEnter={() => setDropdownOpen(true)}
          onMouseLeave={() => setDropdownOpen(false)}
        >
          {algoMenuItems
            .filter(({ show = true }) => show)
            .map(item => {
              return (
                <DeltaButton
                  data-testid={`algoMenu-${item.id}`}
                  key={`algoMenu-${item.id}`}
                  onClick={() => onItemClick(item)}
                  className={styles.menuBtn}
                  wrapperClassName={styles.menuItem}
                  useBasicButton
                >
                  {item.icon}
                  <Box className={styles.menuItemText}>
                    <Box className={styles.heading}>{item.label}</Box>
                    <Box className={styles.description}>{item.description}</Box>
                  </Box>
                  <AngleRightIcon className={styles.rightArrowIcon} />
                </DeltaButton>
              );
            })}
        </Box>
      </Render>
    </Box>
  );
};
export default AlgoMenuIndia;
